<template src="./DigitalReport.html"></template>

<script>
import moment from 'moment'
import {
  getDigitalReport,
  saveDigitalReportSection,
  previewDigitalReport,
  generateDigitalReport,
  getDigitalReportFile,
  getPreviousReportSection,
  importProcessFileToReport,
  getFilesByProcess
} from '../../services'
import ReportField from '../../components/DigitalReport/ReportField'
import { fieldCols } from '../../components/DigitalReport/layout'
import { fillEmptyAnswers } from '../../components/DigitalReport/merge'

const AUTOSAVE_DELAY = 1200
const RECENT_KEY = 'digitalReport.recent'
const RECENT_MAX = 5

const ageFrom = (birthDate) => {
  const birth = moment(birthDate, 'YYYY-MM-DD', true)
  if (!birth.isValid() || birth.isAfter(moment())) return null
  return moment().diff(birth, 'years')
}

// Per-browser shortcut list; storage may be unavailable (private mode), so failures are ignored.
const readRecent = () => {
  try {
    const items = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]')
    return Array.isArray(items) ? items : []
  } catch (err) {
    return []
  }
}
const writeRecent = (items) => {
  try { localStorage.setItem(RECENT_KEY, JSON.stringify(items)) } catch (err) { /* ignore */ }
}

// Empty rows so a list shows its first item(s) ready to fill, as the paper form does.
const withListDefaults = (sections, answers) => {
  const result = {}
  sections.forEach(section => {
    const sectionAnswers = { ...(answers[section.key] || {}) }
    section.groups.forEach(group => group.fields.forEach(field => {
      if (field.type === 'LIST' && !(sectionAnswers[field.key] || []).length) {
        sectionAnswers[field.key] = Array.from({ length: field.minItems || 0 }, () => ({}))
      }
    }))
    result[section.key] = sectionAnswers
  })
  return result
}

export default {
  name: 'DigitalReport',
  components: { ReportField },
  data: () => ({
    processInput: '',
    recent: [],
    loadError: '',
    process: null,
    sections: [],
    storageFolder: '',
    report: null,
    answers: {},
    sectionState: {},
    missing: [],
    generatedFiles: [],
    previousReports: [],
    reusing: false,
    completing: false,
    // Sections this user is responsible for / can still write, and why the report is read only (from the API).
    ownSections: [],
    editableSections: [],
    canGenerate: true,
    lock: null,
    // Answers of the previous report per section, fetched in the background to know what they would fill.
    previousAnswers: {},
    processFiles: null,
    processFilesError: false,
    importing: {},
    activeKey: null,
    timers: {},
    saving: {},
    dirty: {},
    lastSavedAt: null,
    saveError: false,
    previewing: false,
    generating: false,
    confirmDialog: false
  }),
  computed: {
    processId () { return this.$route.params.processId },
    isLoading () { return Boolean(this.processId) && !this.report && !this.loadError },
    activeIndex () { return this.sections.findIndex(s => s.key === this.activeKey) },
    activeSection () { return this.sections[this.activeIndex] || null },
    // The progress counts only the sections this user answers, not the ones they just read.
    mySections () { return this.sections.filter(s => this.ownSections.includes(s.key)) },
    seesWholeReport () { return this.mySections.length === this.sections.length },
    canEditActive () { return Boolean(this.activeSection) && this.editableSections.includes(this.activeSection.key) },
    completedCount () { return this.mySections.filter(s => this.isCompleted(s.key)).length },
    progress () { return this.mySections.length ? (this.completedCount / this.mySections.length) * 100 : 0 },
    missingBySection () {
      return this.missing.reduce((acc, item) => {
        acc[item.section] = [...(acc[item.section] || []), item.label]
        return acc
      }, {})
    },
    // Most recent previous report of the same evaluated that has answers for the active section.
    previousForActive () {
      if (!this.activeSection || !this.activeSection.reusable) return null
      return this.previousReports.find(r => r.sections.includes(this.activeSection.key)) || null
    },
    // Empty fields of the active section that the previous report would fill; the notice only shows when > 0.
    reuseFillable () {
      const source = this.previousAnswers[this.activeKey]
      if (!this.previousForActive || !source) return 0
      const fields = this.activeSection.groups.flatMap(g => g.fields)
      return fillEmptyAnswers(fields, this.answers[this.activeKey], source).filled
    },
    activeFilesField () {
      if (!this.activeSection) return null
      return this.activeSection.groups.flatMap(g => g.fields).find(f => f.type === 'FILES') || null
    },
    importedFileIds () {
      if (!this.activeFilesField) return []
      const files = this.answers[this.activeSection.key][this.activeFilesField.key] || []
      return files.map(f => f.sourceFileId).filter(Boolean)
    },
    generatedFilesDesc () { return [...this.generatedFiles].sort((a, b) => b.version - a.version) },
    pendingSaves () {
      return Object.keys(this.dirty).some(k => this.dirty[k]) || Object.keys(this.saving).some(k => this.saving[k])
    },
    saveIndicator () {
      if (this.saveError) return { icon: 'mdi-alert-circle', color: 'error', text: 'Error al guardar, reintentando…' }
      if (this.pendingSaves) return { icon: 'mdi-cloud-sync-outline', color: 'grey', text: 'Guardando…' }
      if (this.lastSavedAt) return { icon: 'mdi-cloud-check-outline', color: 'success', text: `Guardado ${this.lastSavedAt}` }
      return { icon: 'mdi-cloud-outline', color: 'grey', text: 'Sin cambios' }
    }
  },
  watch: {
    // Process ids are numeric: keep digits only, so pasted text like "#18920" still works.
    processInput (value) {
      const digits = String(value || '').replace(/\D/g, '')
      if (digits !== value) this.$nextTick(() => { this.processInput = digits })
    },
    activeKey () {
      this.loadPreviousAnswers()
    },
    activeFilesField (field) {
      if (field && this.processFiles === null) this.loadProcessFiles()
    },
    processId: {
      immediate: true,
      handler () {
        this.recent = readRecent()
        this.load()
      }
    }
  },
  mounted () {
    window.addEventListener('beforeunload', this.onBeforeUnload)
  },
  beforeDestroy () {
    window.removeEventListener('beforeunload', this.onBeforeUnload)
    this.flushAll()
  },
  async beforeRouteLeave (to, from, next) {
    await this.flushAll()
    next()
  },
  methods: {
    colProps (field) { return fieldCols(field) },
    // A group titled after its only long field (e.g. "Concepto Sobre la Vivienda") does not repeat the label.
    hidesLabel (group, field) {
      return Boolean(group.title) && group.fields.length === 1 && ['TEXTAREA', 'MULTI_CHOICE'].includes(field.type)
    },
    formatDateTime (value) { return moment(value).format('DD/MM/YYYY hh:mm a') },
    formatDate (value) { return moment(value).format('DD/MM/YYYY') },
    isPdfName (name) { return /\.pdf$/i.test(name || '') },
    // The PDF can only include PDF, JPG and PNG annexes.
    canImport (file) { return /\.(pdf|jpe?g|png)$/i.test(file.name || '') },
    isCompleted (key) { return Boolean(this.sectionState[key] && this.sectionState[key].completed) },
    isEditable (key) { return this.editableSections.includes(key) },
    isMine (key) { return this.ownSections.includes(key) },
    sectionIcon (key) {
      if (this.isCompleted(key)) return { icon: 'mdi-check-circle', color: 'success' }
      if (!this.isMine(key)) return { icon: 'mdi-eye-outline', color: 'grey' }
      if (this.missingBySection[key]) return { icon: 'mdi-alert-circle-outline', color: 'amber darken-2' }
      return { icon: 'mdi-circle-outline', color: 'grey' }
    },
    // What the user can do with the open section, as a short notice above the form.
    sectionNotice (key) {
      if (this.lock) return this.lock.message
      if (!this.isEditable(key)) {
        return 'Esta sección la diligencia el analista asignado a ese servicio; aquí la ves en solo lectura.'
      }
      return ''
    },
    openProcess () { this.$router.push(`/informe-digital/${this.processInput}`) },
    openRecent (item) { this.$router.push(`/informe-digital/${item.id}`) },
    fromNow (value) { return moment(value).locale('es').fromNow() },
    rememberRecent () {
      const item = {
        id: this.process.id,
        evaluatedName: this.process.evaluatedName,
        companyName: this.process.companyName,
        openedAt: new Date().toISOString()
      }
      writeRecent([item, ...readRecent().filter(r => r.id !== item.id)].slice(0, RECENT_MAX))
    },
    goToSearch () { this.$router.push('/informe-digital') },
    goTo (index) {
      this.activeKey = this.sections[index].key
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    async load () {
      this.loadError = ''
      this.report = null
      if (!this.processId) return
      try {
        const data = await getDigitalReport(this.processId)
        this.process = data.process
        this.sections = data.sections
        this.storageFolder = data.storageFolder
        this.answers = withListDefaults(data.sections, data.report.answers)
        this.sectionState = data.report.sectionState || {}
        this.generatedFiles = data.report.generatedFiles || []
        this.previousReports = data.previousReports || []
        const allKeys = data.sections.map(s => s.key)
        const permissions = data.permissions || {}
        this.ownSections = permissions.ownSections || allKeys
        this.editableSections = permissions.editableSections || allKeys
        this.canGenerate = permissions.canGenerate !== false
        this.lock = permissions.lock || null
        this.previousAnswers = {}
        this.processFiles = null
        this.missing = data.missing || []
        // Opens on the first section the user answers, so an analyst lands on their own work.
        this.activeKey = (this.mySections[0] || this.sections[0] || {}).key || null
        this.report = data.report
        this.rememberRecent()
      } catch (err) {
        console.error('Error on load digital report:', err)
        this.loadError = (err && err.message) || 'No fue posible cargar el informe del proceso.'
      }
    },
    setAnswer (sectionKey, fieldKey, value) {
      if (!this.isEditable(sectionKey)) return
      const previous = this.answers[sectionKey][fieldKey]
      this.$set(this.answers[sectionKey], fieldKey, value)
      // The age follows the birth date unless the analyst typed a different one.
      if (sectionKey === 'personalData' && fieldKey === 'birthDate') {
        const age = this.answers[sectionKey].age
        if (age === null || age === undefined || age === '' || age === ageFrom(previous)) {
          this.$set(this.answers[sectionKey], 'age', ageFrom(value))
        }
      }
      this.scheduleSave(sectionKey)
    },
    async loadPreviousAnswers () {
      const source = this.previousForActive
      const sectionKey = this.activeKey
      if (!source || this.previousAnswers[sectionKey]) return
      try {
        const result = await getPreviousReportSection(this.processId, sectionKey, source.processId)
        this.$set(this.previousAnswers, sectionKey, result.answers || {})
      } catch (err) {
        // Without the previous answers the notice simply stays hidden.
        console.error('Error on load previous digital report section:', err)
      }
    },
    reusePrevious () {
      const source = this.previousForActive
      const section = this.activeSection
      const previous = this.previousAnswers[this.activeKey]
      if (!source || !section || !previous) return
      this.reusing = true
      try {
        const fields = section.groups.flatMap(g => g.fields)
        const { answers, filled } = fillEmptyAnswers(fields, this.answers[section.key], previous)
        this.$set(this.answers, section.key, answers)
        if (filled) this.scheduleSave(section.key, 0)
        this.$swal({
          text: `Se completaron ${filled} campo(s) con el informe del proceso #${source.processId}. Revísalos antes de continuar.`,
          buttons: false,
          icon: 'success',
          timer: 3500
        })
      } catch (err) {
        console.error('Error on reuse previous digital report:', err)
        this.notifyError(err, 'No fue posible traer los datos del informe anterior.')
      } finally {
        this.reusing = false
      }
    },
    async loadProcessFiles () {
      this.processFilesError = false
      try {
        const files = await getFilesByProcess(this.processId)
        this.processFiles = Array.isArray(files) ? files : []
      } catch (err) {
        console.error('Error on load process files:', err)
        this.processFiles = []
        this.processFilesError = true
      }
    },
    async importFile (file) {
      const section = this.activeSection
      const field = this.activeFilesField
      if (!section || !field) return
      this.$set(this.importing, file.id, true)
      try {
        const annex = await importProcessFileToReport(this.processId, file.id)
        const current = this.answers[section.key][field.key] || []
        this.setAnswer(section.key, field.key, [...current, annex])
      } catch (err) {
        console.error('Error on import process file:', err)
        this.notifyError(err, 'No fue posible agregar el archivo al informe.')
      } finally {
        this.$set(this.importing, file.id, false)
      }
    },
    scheduleSave (sectionKey, delay = AUTOSAVE_DELAY) {
      this.$set(this.dirty, sectionKey, true)
      clearTimeout(this.timers[sectionKey])
      this.timers[sectionKey] = setTimeout(() => this.saveSection(sectionKey), delay)
    },
    // Autosave keeps a draft; `requestComplete` is the explicit "Guardar sección", which the API validates.
    async saveSection (sectionKey, requestComplete = false) {
      // One request per section at a time; changes made meanwhile are saved right after.
      if (this.saving[sectionKey]) {
        if (!requestComplete) return
        while (this.saving[sectionKey]) await new Promise(resolve => setTimeout(resolve, 100))
      }
      clearTimeout(this.timers[sectionKey])
      this.$set(this.dirty, sectionKey, false)
      this.$set(this.saving, sectionKey, true)
      let result
      try {
        result = await saveDigitalReportSection(
          this.processId, sectionKey, this.answers[sectionKey], requestComplete ? true : undefined
        )
        this.$set(this.sectionState, sectionKey, result.sectionState)
        this.missing = result.missing || []
        this.lastSavedAt = moment().format('hh:mm a')
        this.saveError = false
      } catch (err) {
        console.error('Error on save digital report section:', err)
        this.saveError = true
        this.$set(this.dirty, sectionKey, true)
        this.timers[sectionKey] = setTimeout(() => this.saveSection(sectionKey), AUTOSAVE_DELAY * 4)
      } finally {
        this.$set(this.saving, sectionKey, false)
      }
      if (this.dirty[sectionKey] && !this.saveError) await this.saveSection(sectionKey)
      return result
    },
    async completeSection () {
      const sectionKey = this.activeKey
      this.completing = true
      try {
        const result = await this.saveSection(sectionKey, true)
        if (!result) {
          this.notifyError(null, 'No fue posible guardar la sección. Intenta de nuevo.')
          return
        }
        const { completion = {} } = result
        let text = 'Sección guardada y marcada como completa.'
        if (!result.sectionState.completed) {
          text = completion.empty
            ? 'Diligencia al menos un dato antes de guardar la sección.'
            : `Se guardó, pero faltan campos obligatorios: ${(completion.missing || []).join(', ')}.`
        }
        this.$swal({ text, buttons: false, icon: result.sectionState.completed ? 'success' : 'warning', timer: 3500 })
      } finally {
        this.completing = false
      }
    },
    async flushAll () {
      const keys = Object.keys(this.dirty).filter(k => this.dirty[k])
      await Promise.all(keys.map(k => this.saveSection(k)))
      // Wait for saves that were already in flight.
      while (Object.keys(this.saving).some(k => this.saving[k])) {
        await new Promise(resolve => setTimeout(resolve, 150))
      }
    },
    onBeforeUnload (event) {
      if (!this.pendingSaves) return
      event.preventDefault()
      event.returnValue = ''
    },
    // Opens the tab synchronously (on the click) so the browser does not block it as a popup.
    openBlankTab () {
      const tab = window.open('', '_blank')
      if (tab) tab.document.write('<p style="font-family:sans-serif;padding:24px">Generando el informe…</p>')
      return tab
    },
    showInTab (tab, url) {
      if (tab) tab.location.href = url
      else window.open(url, '_blank')
    },
    notifyError (err, fallback) {
      this.$swal({ text: (err && err.message) || fallback, buttons: false, icon: 'error', timer: 3500 })
    },
    async preview () {
      const tab = this.openBlankTab()
      this.previewing = true
      try {
        await this.flushAll()
        const blob = await previewDigitalReport(this.processId)
        this.showInTab(tab, URL.createObjectURL(new Blob([blob], { type: 'application/pdf' })))
      } catch (err) {
        if (tab) tab.close()
        console.error('Error on preview digital report:', err)
        this.notifyError(err, 'No fue posible generar la vista previa.')
      } finally {
        this.previewing = false
      }
    },
    async askGenerate () {
      await this.flushAll()
      if (this.missing.length) {
        this.confirmDialog = true
        return
      }
      this.generate()
    },
    async generate () {
      const tab = this.openBlankTab()
      this.generating = true
      try {
        await this.flushAll()
        const result = await generateDigitalReport(this.processId)
        this.generatedFiles = result.generatedFiles
        this.showInTab(tab, result.url)
        this.$swal({ text: `Se generó la versión ${result.file.version} del informe.`, buttons: false, icon: 'success', timer: 3000 })
      } catch (err) {
        if (tab) tab.close()
        console.error('Error on generate digital report:', err)
        this.notifyError(err, 'No fue posible generar el PDF.')
      } finally {
        this.generating = false
      }
    },
    async openVersion (file) {
      const tab = this.openBlankTab()
      try {
        const result = await getDigitalReportFile(this.processId, file.version)
        this.showInTab(tab, result.url)
      } catch (err) {
        if (tab) tab.close()
        this.notifyError(err, 'No fue posible abrir el PDF.')
      }
    }
  }
}
</script>

<style lang="scss" scoped src="./DigitalReport.scss"></style>
